import * as THREE from 'three'
import { MunichNeighborhood } from './MunichBuildings'
import { munichBuildingTypes, type MunichBuildingType } from './munichNeighborhood'
import { neighborhoodThemes, neighborhoodKind, neighborhoodSeed, neighborhoodPropertyIds, type NeighborhoodCity } from './neighborhoodThemes'
import { parisMansardGeometry } from './parisArchitecture'

/** The accepted sixteen structural types, with city-specific roofs, facades and street details. */
export class CityNeighborhood extends MunichNeighborhood {
  private profile
  private mansard=parisMansardGeometry()
  private activeKind:MunichBuildingType='merchant'
  readonly usedTypes=new Set<MunichBuildingType>()
  readonly parcels:{x:number;z:number;radius:number}[]=[]
  constructor(readonly city:NeighborhoodCity){super();this.profile=neighborhoodThemes[city];this.geometries.add(this.mansard)}
  protected override mat(color:string,metal=0,glow=0){
    const walls=['#dcbfa2','#d5d9c1','#efd5ae','#d4b4a9','#d3c9b0'],i=walls.indexOf(color)
    const p=this.profile
    const mapped=i>=0?p.walls[i]:['#e9ddc5','#f3e9d5'].includes(color)?p.trim:
      color==='#537d81'?p.glass:['#a9634b','#62736f','#687773','#6f8075'].includes(color)?p.roof:
      ['#658170','#5c7d72','#89694d'].includes(color)?p.accent:color
    return super.mat(mapped,metal,glow)
  }
  protected override sign(text:string,x:number,y:number,z:number,w:number,h:number,color?:string,rotation=0){
    const defaults=['BÄCKEREI','BLUMEN','KAFFEEHAUS','ISARHOF','MARKTHALLE','GALERIE','ATELIER','WERKSTATT','ISAR STUDIO','GASTHAUS','LICHTSPIEL','PASSAGE','WINTERGARTEN','BÜRGERHAUS','GARTENHAUS','PAVILLON','LOFTWERK']
    const t=defaults.includes(text)?this.profile.labels[munichBuildingTypes.indexOf(this.activeKind)]:text
    // The vertical cinema sign has four cells; keep it recognizable in each locale.
    const letters=this.city==='moscow'?'КИНО':this.city==='mexico-city'||this.city==='paris'?'CINE':this.city==='venice'?'ARTE':'SHOW'
    super.sign(['K','I','N','O'].includes(t)?letters['KINO'.indexOf(t)]:t,x,y,z,w,h,color,rotation)
  }
  protected override mnRoof(x:number,y:number,z:number,w:number,d:number,h:number,color?:string){
    const city=this.city,p=this.profile
    const industrial=this.activeKind==='market'||this.activeKind==='workshop'||this.activeKind==='loft'
    if((city==='new-york'||city==='las-vegas')&&!industrial){
      this.box(x,y+.07,z,w,.14,d,p.roof)
      for(const s of [-1,1]){this.box(x+s*(w/2-.09),y+.27,z,.15,.45,d,p.trim);this.box(x,y+.27,z+s*(d/2-.09),w,.45,.15,p.trim)}
      if(w>3){this.box(x-.6,y+.39,z-.35,.9,.52,.85,'#83918a');for(let j=0;j<4;j++)this.box(x-.6,y+.67,z-.62+j*.17,.76,.035,.025,'#5c6c67')}
    }else if(city==='paris'&&!industrial){
      const rh=Math.min(1.6,h)
      this.add(this.mansard,this.mat(p.roof,.18),x,y+rh/2,z,w,rh,d)
      if(w>2.7)for(const side of [-1,1])for(const dx of [-w*.25,w*.25]){
        this.box(x+dx,y+rh*.42,z+side*d*.4,.52,.65,.38,p.trim)
        this.box(x+dx,y+rh*.43,z+side*(d*.4+.2),.32,.4,.025,p.glass)
        this.box(x+dx,y+rh*.78,z+side*d*.4,.66,.12,.48,p.roof)
      }
    }else super.mnRoof(x,y,z,w,d,city==='venice'?Math.min(h,1.12):h,industrial&&this.activeKind==='market'?color:p.roof)
  }
  protected override mnWindow(x:number,y:number,z:number,w=.65,h=.92,shutter=false){
    if(this.city==='venice'){
      this.box(x,y,z,w,h,.055,this.profile.glass)
      for(const side of [-1,1]){
        this.box(x+side*(w/2+.055),y-.08,z+.08,.085,h-.12,.15,this.profile.trim)
        this.beam(new THREE.Vector3(x+side*w/2,y+h*.28,z+.1),new THREE.Vector3(x,y+h*.62,z+.1),.052,this.profile.trim)
      }
      this.box(x,y-h/2-.04,z+.12,w+.22,.12,.23,this.profile.trim)
      this.box(x,y-.04,z+.09,.035,h-.13,.04,this.profile.trim)
    }else{
      super.mnWindow(x,y,z,w,h,(this.city==='mexico-city'||this.city==='moscow')&&shutter)
      if(this.city==='paris'){
        this.box(x,y-h/2+.1,z+.25,w+.1,.025,.035,'#40565a')
        for(const dx of [-w*.4,0,w*.4])this.box(x+dx,y-h/2-.01,z+.25,.025,.22,.03,'#40565a')
      }
      if(this.city==='moscow'){
        this.box(x,y+h/2+.13,z+.07,w+.28,.15,.2,this.profile.trim)
        for(const side of [-1,1])this.beam(new THREE.Vector3(x+side*(w/2+.12),y+h/2+.2,z+.12),new THREE.Vector3(x,y+h/2+.43,z+.12),.045,this.profile.trim)
      }
    }
  }
  private rooftopTank(x:number,y:number,z:number){
    for(const dx of [-.32,.32])for(const dz of [-.32,.32])this.box(x+dx,y+.35,z+dz,.055,.7,.055,'#485955')
    this.disc(x,y+1.1,z,.52,1,'#8c7253')
    for(const yy of [y+.68,y+1.02,y+1.43])this.disc(x,yy,z,.535,.045,'#4f5f5d')
    this.add(this.cone,this.mat('#52615c'),x,y+1.77,z,.6,.4,.6,Math.PI/4)
  }
  private nyTower(kind:MunichBuildingType,seed:number,level:number,label?:string){
    const heights={modern:19,corner:13,mansion:15,clockhouse:17,loft:11},h=heights[kind as keyof typeof heights]+seed%3+level*.45
    const glazed=kind==='modern',facade=this.facade(glazed?'blue':kind==='loft'?'rose':'ivory')
    this.box(0,.23,0,5.6,.2,5.8,'#c6bea9')
    const tiers=kind==='clockhouse'?4:kind==='mansion'?3:kind==='modern'?2:1
    for(let i=0;i<tiers;i++){
      const w=4.6-i*.7,d=4.45-i*.55,hh=h/tiers,y=.35+i*hh
      this.box(0,y+hh/2,0,w,hh,d,facade)
      this.box(0,y+hh,0,w+.14,.2,d+.14,this.profile.trim)
      for(const side of [-1,1])this.box(side*(w/2-.1),y+hh/2,d/2+.06,.14,hh,.14,this.profile.trim)
      if(glazed)for(let x=-w/2+.3;x<w/2;x+=.55)this.box(x,y+hh/2,d/2+.04,.035,hh,.04,'#9cb8b7')
    }
    if(kind==='corner'){
      this.disc(1.55,h/2+.35,1.55,.84,h,this.profile.walls[1])
      for(let y=1.1;y<h;y+=1.3)for(let side=0;side<5;side++)this.at(1.55,1.55,1,(side-2)*Math.PI/4,()=>super.mnWindow(0,y,.85,.32,.72))
      this.disc(1.55,h+.49,1.55,.95,.2,this.profile.trim)
    }
    if(kind==='clockhouse'){this.box(0,h+.85,0,.75,1.6,.8,this.profile.trim);this.disc(0,h+2.2,0,.045,1.5,'#a2b7b0')}
    else this.rooftopTank(-.7,h+.15,-.8)
    this.box(0,1.2,2.29,3.9,1.6,.08,'#3f6268');this.sign(label??this.profile.labels[munichBuildingTypes.indexOf(kind)],0,2.18,2.38,4,.33)
    if(kind==='loft')this.fireEscape(h)
  }
  private fireEscape(h:number){
    for(let y=2.55;y<h-.5;y+=1.65){
      this.box(-.75,y,2.49,1.65,.08,.53,'#465953')
      for(const x of [-1.54,.03])this.box(x,y+.31,2.75,.035,.6,.035,'#465953')
      this.box(-.75,y+.6,2.75,1.65,.035,.035,'#465953')
      if(y+1.65<h-.5){for(const x of [-1.4,-1.08])this.box(x,y+.85,2.63,.035,1.7,.04,'#465953');for(let k=0;k<7;k++)this.box(-1.24,y+.14+k*.23,2.63,.35,.035,.04,'#465953')}
    }
  }
  private accents(kind:MunichBuildingType,seed:number){
    const p=this.profile
    if(this.city==='new-york'){
      if(['merchant','courtyard','inn','arcade','terraces'].includes(kind))this.fireEscape(kind==='inn'?3.8:5.4)
      for(const x of [-1.9,1.9])this.box(x,.46,2.63,.35,.24,.37,'#80786a')
    }else if(this.city==='las-vegas'){
      // Slim neon fascia, geometric entry canopies and desert planters.
      for(const y of [1.95,2.12])this.box(0,y,2.54,4.4,.045,.04,this.mat(seed%2?'#e8ae8f':'#8fd7cc',.1,.65))
      for(const x of [-2.4,2.4]){this.disc(x,.45,2.45,.22,.3,'#b7926b');this.disc(x,.99,2.45,.085,.95,'#759269');this.box(x+.12,1.14,2.45,.24,.11,.11,'#759269')}
    }else if(this.city==='mexico-city'){
      for(let i=0;i<14;i++)this.box(-2.15+i*.33,.74,2.54,.2,.2,.035,i%2?p.accent:'#e8ce7f',Math.PI/4)
      for(const x of [-2.4,2.4]){this.disc(x,.43,2.43,.19,.27,'#b9795b');this.ball(x,.77,2.43,.27,'#aa80b1',.85)}
    }else if(this.city==='paris'){
      if(!['rotunda','conservatory','courtyard'].includes(kind)){
        this.box(0,2.31,2.4,4.4,.12,.44,p.trim)
        for(let x=-2.05;x<=2.05;x+=.22)this.box(x,2.58,2.61,.027,.43,.035,'#40565a')
        this.box(0,2.8,2.61,4.4,.045,.04,'#40565a')
      }
      for(const x of [-2.4,2.4]){this.disc(x,.42,2.44,.21,.3,'#817668');this.ball(x,.77,2.44,.25,'#69836a')}
    }else if(this.city==='moscow'){
      if(!['rotunda','conservatory'].includes(kind)){
        for(const x of [-2.18,2.18]){this.box(x,1.47,2.45,.22,2.18,.18,p.trim);this.box(x,2.54,2.45,.36,.16,.3,p.trim)}
        this.box(0,2.62,2.4,4.55,.2,.3,p.trim)
      }
    }else{
      // Venice: stone waterline, paired mooring poles, and an upper loggia.
      this.box(0,.52,2.35,4.65,.34,.24,'#c2b8a2')
      for(const x of [-2.45,2.45]){this.disc(x,.88,2.53,.065,1.3,'#758675');for(const y of [.6,.9,1.2])this.disc(x,y,2.53,.07,.13,'#ecd9b8')}
      if(['mansion','corner','arcade','loft','modern'].includes(kind))for(const x of [-1.5,0,1.5]){
        this.mnArch(x,2.68,2.42,1.15,1.58);this.box(x,2.67,2.5,1.3,.12,.5,p.trim)
        for(const dx of [-.4,0,.4])this.disc(x+dx,2.9,2.72,.025,.4,p.trim)
      }
    }
  }
  private render(kind:MunichBuildingType,seed:number,level=0,label?:string){
    this.activeKind=kind;this.usedTypes.add(kind)
    if(this.city==='new-york'&&['modern','corner','mansion','clockhouse','loft'].includes(kind))this.nyTower(kind,seed,level,label)
    else this.munichBuilding(kind,seed,level,label)
    this.accents(kind,seed)
  }
  place(x:number,z:number,scale:number,rotation:number,index:number){
    // Keep the enlarged facade details from interpenetrating adjacent parcels.
    const radius=2.97*scale
    if(this.parcels.some(p=>Math.abs(x-p.x)<radius+p.radius&&Math.abs(z-p.z)<radius+p.radius))return
    this.parcels.push({x,z,radius})
    this.at(x,z,scale,rotation,()=>this.render(neighborhoodKind(index),neighborhoodSeed(x,z)),true)
  }
  property(group:THREE.Group,tileId:number,level:number,label:string){
    this.render(neighborhoodKind(Math.max(0,neighborhoodPropertyIds.indexOf(tileId))),tileId,level,label);this.flush(group)
  }
  finish(){this.flush(this.root)}
}
